/**
 * TypeScript mirrors of the backend auth DTOs.
 *
 * Source of truth: backend/internal/handlers/auth.go and
 * backend/internal/models/models.go. Field names match the JSON tags
 * exactly — do not rename without changing the backend.
 */

// Role enum — mirrors models.Role. The DB CHECK constraint enforces the
// same set; keep in sync with 001_core_schema.sql.
export type Role =
  | "super_admin"
  | "company_admin"
  | "hr_manager"
  | "manager"
  | "icc_member"
  | "employee"
  | "consultant"
  // G_ACC1 — CA/bookkeeper role for the Accounting module. Membership-
  // scoped like every other role (one person can be an accountant in
  // one tenant and an employee in another). Read+write on /accounting
  // routes only; no HR/POSH/Performance access. Mandatory 2FA in
  // G_ACC7 (mirrors the ICC/complaint strict gate).
  | "accountant";

// Mirrors models.SubscriptionStatus.
export type SubscriptionStatus =
  | "trial"
  | "active"
  | "past_due"
  | "cancelled"
  | "expired";

// Known tenant plan feature flags. The backend stores these as a JSONB
// string list; this union documents the known values but the API may
// return others, so callers should treat unknown strings gracefully.
//
// "accounting" is gated by RequireFeature("accounting") on every
// /api/v1/accounting/* route (G_ACC3a). It is intentionally NOT in
// the trial grant (TrialFeatures, backend/internal/database/tenants.go)
// — accounting pricing lands in G_ACC12, so the nav surface is hidden
// until a tenant explicitly adds the module via override / plan.
export type Feature =
  | "posh"
  | "leave_management"
  | "hr_core"
  | "expense"
  | "performance"
  | "hiring"
  | "time_tracking"
  | "accounting";

// POST /api/v1/auth/register — request body (registerRequest).
// company_slug is OPTIONAL (2026-06-02 UX fix): backend auto-derives
// from company_name when absent. Settings → Workspace lets HR rename
// it later.
export interface RegisterRequest {
  company_name: string;
  company_slug?: string;
  contact_phone?: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
}

// POST /api/v1/auth/login — request body (loginRequest).
//
// ID.2: identity auth — email + password ONLY. There is no
// company_slug: the slug, workspace_required 409 and the explicit
// per-workspace login path are all gone. Which company you act in is
// established AFTER auth via /auth/activate.
export interface LoginRequest {
  email: string;
  password: string;
}

// POST /api/v1/auth/login — 200 response (loginResponse).
//
// totp_required is true when the user already has TOTP enabled: the
// backend issues the access cookie with totp=false, so the frontend
// must route the user to /2fa-verify to upgrade the session before
// reaching any TOTP-gated route.
//
// The backend body carries `csrf_token` — surfaced here ONLY so the
// auth-flow handler can write it into `setCSRFToken()` for the
// cross-origin staging deployment where the `wr_csrf` cookie set by the
// backend is host-only and unreadable from the frontend's
// `document.cookie` (see lib/api/client.ts → csrfStore fallback).
// On a same-origin production deploy (e.g. api.workright.in +
// app.workright.in sharing `.workright.in` Domain cookie) the cookie
// path is still authoritative; the in-memory store is only the
// cross-origin escape hatch.
export interface LoginResponse {
  user_id: number;
  tenant_id?: number;
  role: Role;
  totp_required: boolean;
  features: string[];
  csrf_token?: string;
}

// POST /api/v1/auth/register — 201 response. ID.2: register now
// auto-activates the just-created company and issues a FULL
// tenant-scoped session — the body is byte-identical to a
// single-membership loginResponse (may itself be totp_required). The
// old { tenant_id, user_id, message } shape is gone.
export type RegisterResponse = LoginResponse;

// One selectable workspace returned by the multi-company login picker
// and the in-app company switcher. Mirrors backend workspaceChoice
// (auth.go): slug + company name + the caller's own role in it.
export interface WorkspaceChoice {
  slug: string;
  company_name: string;
  role?: Role;
}

// POST /api/v1/auth/login — 200 response when a successful identity
// login resolved to >=2 active memberships. Mirrors backend
// selectWorkspaceResponse: an IDENTITY-ONLY session cookie IS set (no
// active tenant), so the client completes the pick by POSTing the
// chosen slug to /auth/activate with NO re-auth and NO selection token
// (that concept is gone). `totp_required` is a hint that a 2FA step
// applies to this identity (the server still enforces it at activate).
export interface SelectWorkspaceResponse {
  select_workspace: true;
  workspaces: WorkspaceChoice[];
  totp_required: boolean;
}

// The login endpoint returns EITHER the normal session-establishing
// loginResponse OR the workspace-picker body. `select_workspace` is the
// discriminant (absent/false → normal flow).
export type LoginResult = LoginResponse | SelectWorkspaceResponse;

export function isSelectWorkspace(
  r: LoginResult,
): r is SelectWorkspaceResponse {
  return (r as SelectWorkspaceResponse).select_workspace === true;
}

// POST /api/v1/auth/activate — request body (activateRequest). The
// single endpoint that establishes/switches the active tenant: both
// the post-login picker target AND the in-app instant switch (no
// password, no logout). No selection token — the identity session
// cookie is the authority and the backend strictly re-checks
// membership ownership.
export interface ActivateRequest {
  slug: string;
}

// GET /api/v1/auth/my-workspaces — 200 response (myWorkspacesResponse).
// `current` is omitted for a platform/NULL-tenant or identity-only
// caller; `others` is always an array ([] when the identity has no
// other workspaces).
export interface MyWorkspacesResponse {
  current?: WorkspaceChoice;
  others: WorkspaceChoice[];
}

// POST /api/v1/auth/forgot-password — request body (forgotRequest).
// ID.2: forgot/reset operate on the IDENTITY (one password,
// account-wide) — no company_slug.
export interface ForgotPasswordRequest {
  email: string;
}

// POST /api/v1/auth/forgot-password — 200 response (forgotResponse).
// dev_token is only present outside production.
export interface ForgotPasswordResponse {
  message: string;
  dev_token?: string;
}

// POST /api/v1/auth/reset-password — request body (resetRequest).
// ID.2: identity-wide reset — no company_slug.
export interface ResetPasswordRequest {
  email: string;
  token: string;
  new_password: string;
}

// Nested tenant block in GET /api/v1/me (meTenant).
//
// subscription_started_at / subscription_expires_at are additive (QA
// CRIT-2): both nullable — a tenant on no plan with no trial yet has
// neither. A freshly-registered tenant gets a 14-day trial, so a new
// signup has subscription_status "trial" + an expires_at the dashboard
// derives the "Trial — N days left" banner from. The resolved feature
// set stays MeResponse.features (drive module gating off that, never a
// hard-coded client list).
export interface MeTenant {
  id: number;
  name: string;
  slug: string;
  subscription_status: SubscriptionStatus;
  subscription_started_at?: string;
  subscription_expires_at?: string;
}

// GET /api/v1/me — 200 response (meResponse).
//
// totp_required here is models.User.RequiresTOTP() — i.e. whether the
// role *forces* TOTP enrolment (super_admin, hr_manager). Distinct from
// the login response field of the same name (which means "already
// enabled"). totp_enabled reports whether the user actually has it on.
// ID.2: identity_id is always present (the authenticated human).
// user_id / tenant_id / role / tenant are ONLY present when the
// session has an ACTIVE membership — an identity-only session (the
// >=2-membership picker state, before /auth/activate) has none of
// them, so the dashboard guard treats "no tenant_id" as "must pick a
// workspace first".
export interface MeResponse {
  identity_id: number;
  user_id: number;
  tenant_id?: number;
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  totp_enabled: boolean;
  totp_required: boolean;
  features: string[];
  // G_ACC9: capability tokens for the active membership (mirrors
  // users.capabilities JSONB from migration 027). Always an array
  // (never null) — empty for identity-only / platform sessions. The
  // only token in v1 is "override_lock" (period-unlock on the
  // Accounting period-locks admin page).
  capabilities: string[];
  tenant?: MeTenant;
  // HR-review must-fix #3: DPDP 2023 per-membership employee consent.
  // True ONLY when there's an active tenant membership AND the
  // backend has NOT recorded a consent yet (`users.consent_given =
  // FALSE`). The dashboard fires a one-time non-dismissible modal
  // when this is true; POST /api/v1/auth/dpdp-consent flips it.
  // False / omitted for identity-only / platform sessions.
  needs_dpdp_consent?: boolean;
}

/**
 * The EXACT DPDP-purpose string the backend stores in
 * `users.consent_purpose` on a successful POST /auth/dpdp-consent
 * (mirrors `handlers.auth.go` `employeeDPDPConsentPurpose`). Stored
 * value == shown value, same lockstep as the public-careers consent
 * purpose. Editing this string here without bumping the backend
 * const breaks the lockstep — keep them in sync.
 */
export const EMPLOYEE_DPDP_CONSENT_PURPOSE =
  "Processing of my personal data (PII, contact details, identity proofs, financial and bank details, employment contract, compensation, documents, attendance, leave and performance records) by my employer for the purposes of employment administration, payroll, statutory compliance (PF, ESI, professional tax, income tax, POSH Act 2013) and lawful reporting, for the duration of my employment and the statutory retention period thereafter (up to 7 years for Indian labour and tax records). This consent is recorded in compliance with the Digital Personal Data Protection Act, 2023.";

// POST /api/v1/auth/totp/setup — 200 response (totpSetupResponse).
export interface TOTPSetupResponse {
  secret: string;
  otpauth_url: string;
  backup_codes: string[];
}

// POST /api/v1/auth/totp/verify — request body (totpVerifyRequest).
export interface TOTPVerifyRequest {
  code: string;
}

// POST /api/v1/auth/totp/disable — request body (totpDisableRequest).
export interface TOTPDisableRequest {
  current_password: string;
  code: string;
}

// Standard backend error envelope. Every writeError() call emits this
// shape: { error, message, field }. `field` maps to a form input when
// the error is a validation failure.
export interface ApiError {
  error: string;
  message: string;
  field?: string;
}
